import {
  IsString,
  IsEnum,
  IsOptional,
  IsArray,
  IsUUID,
  IsEmail,
  IsObject,
  ValidateNested,
  MinLength,
  MaxLength,
  IsDateString,
} from 'class-validator'
import { Type } from 'class-transformer'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { EmailPriority, EmailCategory } from '../../../enums'

export class CreateEmailRecipientDto {
  @ApiProperty({
    description: 'Email du destinataire',
    example: 'parent@example.com',
  })
  @IsEmail()
  email!: string

  @ApiPropertyOptional({
    description: 'Nom du destinataire',
    example: 'Jean Dupont',
  })
  @IsString()
  @IsOptional()
  name?: string

  @ApiPropertyOptional({ description: 'ID utilisateur si connu' })
  @IsUUID()
  @IsOptional()
  userId?: string

  @ApiPropertyOptional({ description: 'ID établissement du destinataire' })
  @IsUUID()
  @IsOptional()
  establishmentId?: string

  @ApiPropertyOptional({
    description: 'Rôle du destinataire (depuis @sphere/auth)',
    example: 'student',
  })
  @IsString()
  @IsOptional()
  role?: string
}

/**
 * DTO for creating/sending an email
 */
export class CreateEmailDto {
  @ApiProperty({
    description: "Sujet de l'email",
    example: 'Nouvelle annonce importante',
  })
  @IsString()
  @MinLength(3)
  @MaxLength(255)
  subject!: string

  @ApiProperty({
    description: 'Contenu texte brut',
    example: 'Bonjour, voici le message...',
  })
  @IsString()
  @MinLength(10)
  body!: string

  @ApiPropertyOptional({ description: 'Contenu HTML (optionnel)' })
  @IsString()
  @IsOptional()
  bodyHtml?: string

  @ApiProperty({
    description: 'Liste des destinataires',
    type: [CreateEmailRecipientDto],
  })
  @ValidateNested({ each: true })
  @Type(() => CreateEmailRecipientDto)
  @IsArray()
  recipients!: CreateEmailRecipientDto[]

  @ApiPropertyOptional({ enum: EmailPriority, default: EmailPriority.NORMAL })
  @IsEnum(EmailPriority)
  @IsOptional()
  priority?: EmailPriority = EmailPriority.NORMAL

  @ApiPropertyOptional({ enum: EmailCategory })
  @IsEnum(EmailCategory)
  @IsOptional()
  category?: EmailCategory

  @ApiPropertyOptional({ description: "ID de l'établissement concerné" })
  @IsUUID()
  @IsOptional()
  establishmentId?: string

  @ApiPropertyOptional({
    description: "Type d'entité liée",
    example: 'announcement',
  })
  @IsString()
  @IsOptional()
  relatedType?: string

  @ApiPropertyOptional({ description: "ID de l'entité liée" })
  @IsUUID()
  @IsOptional()
  relatedId?: string

  @ApiPropertyOptional({ description: 'ID du template à utiliser' })
  @IsUUID()
  @IsOptional()
  templateId?: string

  @ApiPropertyOptional({
    description: 'Données pour template (JSON)',
    example: { userName: 'Jean', courseName: 'Mathématiques' },
  })
  @IsObject()
  @IsOptional()
  templateData?: Record<string, any>

  @ApiPropertyOptional({
    description: "Date d'envoi planifié",
    example: '2025-01-15T10:00:00Z',
  })
  @IsDateString()
  @IsOptional()
  scheduledAt?: string

  @ApiPropertyOptional({ description: 'ID de la campagne associée' })
  @IsUUID()
  @IsOptional()
  campaignId?: string
}
